import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Tooltip,
  message,
  Empty
} from 'antd';
import {
  FireOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Line } from '@ant-design/plots';
import { environmentService, AirQualityData, StatsQuery } from '../../../services/environmentService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;
const { RangePicker } = DatePicker;

interface AirQualityMonitorProps {
  onRefresh?: () => void;
}

const AirQualityMonitor: React.FC<AirQualityMonitorProps> = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [airQualityData, setAirQualityData] = useState<AirQualityData[]>([]);
  const [filteredData, setFilteredData] = useState<AirQualityData[]>([]);
  const [statsData, setStatsData] = useState<any>(null);
  const [filters, setFilters] = useState<StatsQuery>({
    area: '',
    floor: undefined,
    period: 'daily'
  });

  // 載入空氣品質數據
  const loadAirQualityData = async () => {
    try {
      setLoading(true);
      const [data, stats] = await Promise.all([
        environmentService.getAirQualityData(filters),
        environmentService.getAirQualityStats(filters)
      ]);
      setAirQualityData(data);
      setFilteredData(data);
      setStatsData(stats);
    } catch (error) {
      console.error('載入空氣品質數據失敗:', error);
      message.error('載入空氣品質數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 處理過濾器變更
  const handleFilterChange = (key: keyof StatsQuery, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // 獲取空氣品質等級
  const getAQILevel = (aqi: number) => {
    if (aqi <= 50) return { level: '優秀', color: '#52c41a', desc: '空氣品質令人滿意' };
    if (aqi <= 100) return { level: '良好', color: '#faad14', desc: '空氣品質可接受' };
    if (aqi <= 150) return { level: '中等', color: '#fa8c16', desc: '敏感人群需注意' };
    if (aqi <= 200) return { level: '差', color: '#f5222d', desc: '對健康有害' };
    return { level: '危險', color: '#722ed1', desc: '對所有人都有害' };
  };

  // 獲取溫度顏色
  const getTemperatureColor = (temp: number) => {
    if (temp < 18) return '#1890ff'; // 冷
    if (temp < 25) return '#52c41a'; // 適中
    if (temp < 30) return '#faad14'; // 溫暖
    return '#f5222d'; // 熱
  };

  // 獲取濕度顏色
  const getHumidityColor = (humidity: number) => {
    if (humidity < 40) return '#fa8c16'; // 乾燥
    if (humidity < 70) return '#52c41a'; // 適中
    return '#1890ff'; // 潮濕
  };

  // 表格列定義
  const columns: ColumnsType<AirQualityData> = [
    {
      title: '感測器',
      dataIndex: 'sensorId',
      key: 'sensorId',
      width: 120
    },
    {
      title: '位置',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <span>
          {record.location.area} - {record.location.floor}F
          {record.location.zone && ` - ${record.location.zone}`}
        </span>
      )
    },
    {
      title: 'AQI',
      key: 'aqi',
      width: 100,
      render: (_, record) => {
        const { level, color } = getAQILevel(record.classification.airQualityIndex);
        return (
          <Tooltip title={`空氣品質: ${level}`}>
            <Tag color={color}>{record.classification.airQualityIndex}</Tag>
          </Tooltip>
        );
      },
      sorter: (a, b) => a.classification.airQualityIndex - b.classification.airQualityIndex
    },
    {
      title: '溫度 (°C)',
      key: 'temperature',
      width: 100,
      render: (_, record) => (
        <span style={{ color: getTemperatureColor(record.measurements.temperature.current) }}>
          {record.measurements.temperature.current}°C
        </span>
      ),
      sorter: (a, b) => a.measurements.temperature.current - b.measurements.temperature.current
    },
    {
      title: '濕度 (%)',
      key: 'humidity',
      width: 100,
      render: (_, record) => (
        <span style={{ color: getHumidityColor(record.measurements.humidity.current) }}>
          {record.measurements.humidity.current}%
        </span>
      ),
      sorter: (a, b) => a.measurements.humidity.current - b.measurements.humidity.current
    },
    {
      title: 'CO₂ (ppm)',
      key: 'co2',
      width: 100,
      render: (_, record) => (
        <span style={{ 
          color: record.measurements.co2.current > 1000 ? '#f5222d' : '#52c41a' 
        }}>
          {record.measurements.co2.current}
        </span>
      ),
      sorter: (a, b) => a.measurements.co2.current - b.measurements.co2.current
    },
    {
      title: 'PM2.5 (μg/m³)',
      key: 'pm25',
      width: 120,
      render: (_, record) => (
        <span style={{ 
          color: record.measurements.pm25.current > 35 ? '#f5222d' : '#52c41a' 
        }}>
          {record.measurements.pm25.current}
        </span>
      ),
      sorter: (a, b) => a.measurements.pm25.current - b.measurements.pm25.current
    },
    {
      title: '更新時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (timestamp: string) => new Date(timestamp).toLocaleString('zh-TW'),
      sorter: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      defaultSortOrder: 'descend'
    }
  ];

  // 準備圖表數據
  const prepareChartData = () => {
    if (!airQualityData.length) return [];
    
    return airQualityData.map(item => ({
      time: new Date(item.timestamp).toLocaleString('zh-TW', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      AQI: item.classification.airQualityIndex,
      溫度: item.measurements.temperature.current,
      濕度: item.measurements.humidity.current,
      CO2: item.measurements.co2.current / 10, // 縮放以便顯示
      PM25: item.measurements.pm25.current,
      location: `${item.location.area}-${item.location.floor}F`
    }));
  };

  const chartConfig = {
    data: prepareChartData(),
    xField: 'time',
    yField: 'value',
    seriesField: 'category',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 1000,
      },
    },
    color: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'],
  };

  useEffect(() => {
    loadAirQualityData();
  }, [filters]);

  // 計算統計數據
  const calculateStats = () => {
    if (!airQualityData.length) return null;

    const avgAQI = airQualityData.reduce((sum, item) => sum + item.classification.airQualityIndex, 0) / airQualityData.length;
    const avgTemp = airQualityData.reduce((sum, item) => sum + item.measurements.temperature.current, 0) / airQualityData.length;
    const avgHumidity = airQualityData.reduce((sum, item) => sum + item.measurements.humidity.current, 0) / airQualityData.length;
    const avgCO2 = airQualityData.reduce((sum, item) => sum + item.measurements.co2.current, 0) / airQualityData.length;
    const alertCount = airQualityData.filter(item => item.alerts.length > 0).length;

    return { avgAQI, avgTemp, avgHumidity, avgCO2, alertCount };
  };

  const stats = calculateStats();
  const aqiInfo = stats ? getAQILevel(stats.avgAQI) : null;

  return (
    <div className="air-quality-monitor">
      {/* 過濾器 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <Select
              placeholder="選擇區域"
              value={filters.area}
              onChange={(value) => handleFilterChange('area', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="A區">A區</Option>
              <Option value="B區">B區</Option>
              <Option value="C區">C區</Option>
              <Option value="地下停車場">地下停車場</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="選擇樓層"
              value={filters.floor}
              onChange={(value) => handleFilterChange('floor', value)}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value={-1}>B1</Option>
              <Option value={1}>1F</Option>
              <Option value={2}>2F</Option>
              <Option value={3}>3F</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Select
              value={filters.period}
              onChange={(value) => handleFilterChange('period', value)}
              style={{ width: '100%' }}
            >
              <Option value="hourly">每小時</Option>
              <Option value="daily">每日</Option>
              <Option value="weekly">每週</Option>
              <Option value="monthly">每月</Option>
            </Select>
          </Col>
          <Col span={8}>
            <RangePicker
              value={filters.startDate && filters.endDate ? [
                dayjs(filters.startDate),
                dayjs(filters.endDate)
              ] : null}
              onChange={(dates) => {
                if (dates) {
                  handleFilterChange('startDate', dates[0]?.format('YYYY-MM-DD'));
                  handleFilterChange('endDate', dates[1]?.format('YYYY-MM-DD'));
                } else {
                  handleFilterChange('startDate', '');
                  handleFilterChange('endDate', '');
                }
              }}
              style={{ width: '100%' }}
            />
          </Col>
          <Col span={4}>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={loadAirQualityData}
              loading={loading}
              style={{ width: '100%' }}
            >
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 統計概覽 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均空氣品質指數"
                value={stats.avgAQI}
                precision={0}
                valueStyle={{ color: aqiInfo?.color }}
                prefix={<EnvironmentOutlined />}
                suffix="AQI"
              />
              <div style={{ marginTop: 8 }}>
                <Tag color={aqiInfo?.color}>{aqiInfo?.level}</Tag>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                  {aqiInfo?.desc}
                </div>
              </div>
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均溫度"
                value={stats.avgTemp}
                precision={1}
                valueStyle={{ color: getTemperatureColor(stats.avgTemp) }}
                prefix={<FireOutlined />}
                suffix="°C"
              />
              <Progress
                percent={(stats.avgTemp / 40) * 100}
                strokeColor={getTemperatureColor(stats.avgTemp)}
                showInfo={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="平均濕度"
                value={stats.avgHumidity}
                precision={1}
                valueStyle={{ color: getHumidityColor(stats.avgHumidity) }}
                suffix="%"
              />
              <Progress
                percent={stats.avgHumidity}
                strokeColor={getHumidityColor(stats.avgHumidity)}
                showInfo={false}
                size="small"
                style={{ marginTop: 8 }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="警報數量"
                value={stats.alertCount}
                valueStyle={{ color: stats.alertCount > 0 ? '#f5222d' : '#52c41a' }}
                prefix={<ExclamationCircleOutlined />}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                平均CO₂: {stats.avgCO2.toFixed(0)} ppm
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* 趨勢圖表 */}
      <Card title="空氣品質趨勢" style={{ marginBottom: 16 }}>
        {prepareChartData().length > 0 ? (
          <Line
            {...chartConfig}
            data={prepareChartData().flatMap(item => [
              { time: item.time, value: item.AQI, category: 'AQI', location: item.location },
              { time: item.time, value: item.溫度, category: '溫度', location: item.location },
              { time: item.time, value: item.濕度, category: '濕度', location: item.location },
              { time: item.time, value: item.CO2, category: 'CO₂(×10)', location: item.location },
              { time: item.time, value: item.PM25, category: 'PM2.5', location: item.location }
            ])}
            height={300}
          />
        ) : (
          <Empty description="暫無數據" />
        )}
      </Card>

      {/* 詳細數據表格 */}
      <Card title="感測器詳細數據">
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          loading={loading}
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 項，共 ${total} 項`
          }}
          locale={{
            emptyText: <Empty description="暫無空氣品質數據" />
          }}
        />
      </Card>
    </div>
  );
};

export default AirQualityMonitor;